import { Injectable } from "@angular/core"
import { FireStoreService } from "@shared/service/fire-store.service"
import { PersonDTO, PersonService } from "@app/pages/person/person.service"
import { REFERENTIAL, ReferentialDTO, ReferentialService } from "@app/pages/referential/referential.service"
import { ProgramConfigurationDTO, ProgramDTO, ProgramService } from "@app/pages/program/program.service"
import { arrayRemove, arrayUnion } from "@angular/fire/firestore"
import { AuthService } from "@app/auth"
import { UnitDTO } from "@app/pages/unit/unit.service"

export interface ExternViewInfo {
  list: Array<PerformanceInfoExtern>
  persons: { [personId: string]: string /*firstName*/ }
}

@Injectable({
  providedIn: "root",
})
export class PerformanceService extends FireStoreService<PerformanceDTO> {
  getPerformancesByProjectsIdCloudFunction = this.functions.httpsCallable("getPerformancesByProjectsId")
  getProjectsByDateCloudFunction = this.functions.httpsCallable("getProjectsCodeByDate")

  constructor(
    private personService: PersonService,
    private programService: ProgramService,
    private refService: ReferentialService,
    private authService: AuthService,
  ) {
    super()
    this.init({
      collectionKey: "performances",
      fullTextFields: [
        "season_label",
        "code_label",
        "status_label",
        "type_label",
        "town",
        "country",
        "program_label",
        "director_label",
        "artistic_director_label",
        "video_notes",
        "note",
      ],
      sorting: {
        field: "date",
        order: "desc",
      },
    })
  }

  public formatName(dto: PersonDTO | undefined, personId: string): string {
    return dto ? `${dto.fullName}` : personId ? "xxx xxx" /*no rights*/ : ""
  }

  updatingLegacyDistribution(dto: PerformanceDTO) {
    // managing legacy distribution
    if (dto.distribution) {
      const distribution = dto.distribution
      Object.keys(distribution).forEach((key) => {
        if (distribution[key]) {
          Object.keys(distribution[key]).forEach((personId) => {
            const dist = distribution[key][
              personId
            ] as any /*as we are managing legacy fields (roleId,roleSubstituteId,coefSubstitute) */
            dist.roles = dist.roles || []
            dist.substitutes = dist.substitutes || []
            if (dist.roleId) {
              dist.roles.push({ id: dist.roleId })
              delete dist["roleId"]
            }
            if (dist.roleSubstituteId) {
              dist.substitutes.push({ id: dist.roleSubstituteId, coef: dist.coefSubstitute || 0 })
              delete dist["roleSubstituteId"]
              delete dist["coefSubstitute"]
            }
          })
        }
      })
    }
  }

  toDTO(dto: PerformanceDTO, action: string): Promise<PerformanceDTO> {
    // TO BE IMPROVED

    if (dto.communication) {
      // Updating date into communication / notes
      const comm = dto.communication
      for (const personId in comm) {
        const dataPerson = comm[personId] as any
        if (dataPerson) this._formatDtoDateFromFirestore(dataPerson)
        if (dataPerson?.notes) {
          dataPerson.notes.forEach((note: ExternNote) => this._formatDtoDateFromFirestore(note))
        }
      }
    }
    this.updatingLegacyDistribution(dto)

    return Promise.all([
      this.personService.getAllFromCache(),
      this.refService.getAllFromCache(REFERENTIAL.currency),
      this.refService.getAllFromCache(REFERENTIAL.perf_type),
      this.refService.getAllFromCache(REFERENTIAL.perf_status),
      this.refService.getAllFromCache(REFERENTIAL.project_code),
      this.refService.getAllFromCache(REFERENTIAL.season),
      this.refService.getAllFromCache(REFERENTIAL.perf_status_unexpected),
      this.refService.getAllFromCache(REFERENTIAL.video_storage),
      this.refService.getAllFromCache(REFERENTIAL.skills),
      this.programService.getAllFromCache(),
      // action === FireStoreService.ACTION.GET
      //   ? dto.program?.id
      //     ? this.programService.get(dto.program?.id).then((x) => [x])
      //     : Promise.resolve([])
      //   : this.programService.getAllFromCache(),
    ]).then((refs) => {
      const [
        refPersons,
        refCurrencies,
        refTypes,
        refStatus,
        refCodes,
        refSeasons,
        refStatusUnexpected,
        refVideos,
        refSkills,
        refPrograms,
      ] = refs
      dto.season_label = refSeasons.find((x: ReferentialDTO) => x.id === dto.season?.id)?.name || "?"
      dto.code_label = refCodes.find((x: ReferentialDTO) => x.id === dto.code?.id)?.name || "?"
      dto.type_label = refTypes.find((x: ReferentialDTO) => x.id === dto.type?.id)?.name || "?"
      dto.status_label = refStatus.find((x: ReferentialDTO) => x.id === dto.status?.id)?.name || "-"
      dto.status_label_color = refStatus.find((x: ReferentialDTO) => x.id === dto.status?.id)?.color || ""
      dto.status_unexpected_label =
        refStatusUnexpected.find((x: ReferentialDTO) => x.id === dto.status_unexpected?.id)?.name || ""

      const program = refPrograms.find((x: ProgramDTO) => x.id === dto.program?.id)
      const programVersion = program?.versions?.find((x) => x.id === dto.program_version_id)
      const programVersionV2 = program?.versions?.find((x) => x.isV2 && x.id === dto.programV2?.versionId)
      const programConf = programVersion?.configurations?.find((x) => x.id === dto.program_configuration_id)
      dto.program_label = program?.name
      dto.program_official_label = program?.name_official
      dto.program_version_label = (programVersionV2 || programVersion)?.name
      dto.program_configuration_label = programConf?.name
      dto.name_label = `${dto.code_label} - ${dto.date?.toLocaleDateString("fr-FR", { weekday: "long" })} ${dto.date?.toLocaleDateString("fr-FR")}`
      if (dto.hour_date) {
        dto.name_label += ` à ${dto.hour_date
          ?.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
          .replace(":", "h")}`
        const hours = dto.hour_date.getHours()
        const minutes = dto.hour_date.getMinutes()
        const newDate = new Date(dto.date)
        newDate.setHours(hours, minutes, 0, 0)
        dto.date = newDate // adding hours + minutes to the date!
      }
      const skillIdInterprete = this.refService.getDancerSkillId(refSkills)
      dto.nb_dancer_calculated = Object.values(dto.personsSkills || {}).filter((ids) =>
        ids.includes(skillIdInterprete),
      ).length

      dto.artistic_director_label = this.formatName(
        refPersons.find((x: PersonDTO) => x.id === dto.artistic_director?.id),
        dto.artistic_director?.id,
      )
      dto.director_label = this.formatName(
        refPersons.find((x: PersonDTO) => x.id === dto.director?.id),
        dto.director?.id,
      )
      dto.currency_label = refCurrencies.find((x: ReferentialDTO) => x.id === dto.currency?.id)?.name
      dto.video_storage_label = dto.video_storage
        ?.map((x: any) => refVideos.find((y) => y.id === x.id)?.name)
        .join(", ")
      dto.seniority_ignore_label = dto.seniority_ignore ? "OUI" : ""
      dto.v2_ref = dto.programV2?.active ? "V2" : "V1"
      return dto
    })
  }

  getPerformancesByProject(projectCode: string): Promise<Array<PerformanceDTO>> {
    const projectRef = this.firestore.collection("ref-project-code").doc(projectCode).ref
    return this.firestore
      .collection(this.config.collectionKey, (ref) => ref.where("code", "==", projectRef))
      .get()
      .toPromise()
      .then((res) => {
        const items: Array<Promise<PerformanceDTO>> = []
        res?.forEach((r) => items.push(this._toDTO(r, FireStoreService.ACTION.GET)))
        console.log(`!QUOTA! firestore reading - getPerformancesByProject ${this.config.collectionKey}`, items.length)
        return Promise.all(items).then((items) => {
          items.sort((a, b) => (a.date > b.date ? 1 : -1))
          return items
        })
      })
      .catch((err) => this.handleError(err, null))
  }

  getPerformancesInfoForExternId(
    personId: string,
    hydrating: (data: ExternViewInfo) => Promise<any>,
  ): Promise<ExternViewInfo> {
    const cacheKey = `${this.config.collectionKey}#${personId}#getPerformancesInfoForExternId`
    return this.callCloudFunction(
      () => this.getPerformancesByProjectsIdCloudFunction({ personId }).toPromise(),
      cacheKey,
      true,
      hydrating,
    )
  }

  async getProgramsAndPerformancesByUnit(unit: { id: string }): Promise<
    Array<{
      program: ProgramDTO
      performance: PerformanceDTO
    }>
  > {
    const programs = await this.programService.getAllFromCache()
    const performances = await this.getAllFromCache()
    const filteredProgramVersion: Array<string> = []
    const filteredPrograms = programs.filter((p) => {
      const versions = p.versions?.filter((v) => v.units?.find((u) => u.value?.id === unit.id))
      versions?.forEach((version) => filteredProgramVersion.push(version.id))
      return versions?.length > 0
    })
    const res: Array<{
      program: ProgramDTO
      performance: PerformanceDTO
    }> = []
    filteredPrograms.map((program) =>
      performances
        .filter(
          (pe) =>
            pe.program?.id === program.id &&
            (filteredProgramVersion.includes(pe.program_version_id) ||
              filteredProgramVersion.includes(pe.programV2?.versionId || "")),
        )
        .forEach((performance) => {
          res.push({
            program,
            performance,
          })
        }),
    )
    res.sort((a, b) => (a.performance.date > b.performance.date ? -1 : 1))
    return res
  }

  // représentation entière chargée plus tôt : programV2 exclu, pour ne pas écraser une modification faite dans program2
  updateWithoutProgramV2(perf: PerformanceDTO): Promise<PerformanceDTO> {
    const { programV2, ...rest } = perf
    return this.update(rest)
  }

  getPerformancesByProgram(programId: string): Promise<Array<PerformanceDTO>> {
    const programRef = this.firestore.collection("programs").doc(programId).ref
    return this.firestore
      .collection(this.config.collectionKey, (ref) => ref.where("program", "==", programRef))
      .get()
      .toPromise()
      .then((res) => {
        const items: Array<Promise<PerformanceDTO>> = []
        res?.forEach((r) => items.push(this._toDTO(r, FireStoreService.ACTION.GET)))
        console.log(
          `!QUOTA! firestore reading - getPerformancesByProgram ${this.config.collectionKey} (${programId})`,
          items.length,
        )
        return Promise.all(items).then((items) => {
          items.sort((a, b) => (a.date < b.date ? 1 : -1))
          return items
        })
      })
      .catch((err) => this.handleError(err, null))
  }

  getPerformancesWithDistribution(): Promise<Array<PerformanceDTO>> {
    return this.firestore
      .collection(this.config.collectionKey, (ref) => ref.where("distribution", "!=", null))
      .get()
      .toPromise()
      .then((res) => {
        const items: Array<Promise<PerformanceDTO>> = []
        res?.forEach((r) => items.push(this._toDTO(r, FireStoreService.ACTION.GET)))
        console.log(
          `!QUOTA! firestore reading - getPerformancesWithDistribution ${this.config.collectionKey}`,
          items.length,
        )
        return Promise.all(items).then((items) => {
          items.sort((a, b) => (a.date < b.date ? 1 : -1))
          return items
        })
      })
      .catch((err) => this.handleError(err, null))
  }

  getProjectsByDate(dateStart: Date, dateEnd: Date): Promise<Array<string>> {
    if (this.authService.isAdmin()) {
      // performances are better this way...
      return this.firestore
        .collection(this.config.collectionKey, (ref) => ref.where("date", ">", dateStart).where("date", "<=", dateEnd))
        .get()
        .toPromise()
        .then((res) => {
          let items: Array<Promise<PerformanceDTO>> = []
          res?.forEach((r) => {
            const obj = r.data() as any
            items.push(obj.code.id)
          })
          items = [...new Set(items)]
          console.log(`!QUOTA! firestore reading - getProjectsByDate ${this.config.collectionKey}`, items.length)
          return items
        })
        .catch((err) => this.handleError(err, null))
    }

    const cacheKey = `${
      this.config.collectionKey
    }#${dateStart.toISOString()}#${dateEnd.toISOString()}#getProjectsByDateCloudFunction`
    return this.callCloudFunction(
      () => this.getProjectsByDateCloudFunction({ dateStart, dateEnd }).toPromise(),
      cacheKey,
    )
  }

  getPerformancesByDate(dateStart: Date, dateEnd: Date): Promise<Array<PerformanceDTO>> {
    return this.firestore
      .collection(this.config.collectionKey, (ref) => ref.where("date", ">=", dateStart).where("date", "<=", dateEnd))
      .get()
      .toPromise()
      .then((res) => {
        const promises: Array<Promise<any>> = []
        // const items: Array<PerformanceDTO> = []
        res?.forEach((r) => {
          promises.push(this._toDTO(r, FireStoreService.ACTION.GET))
        })
        console.log(`!QUOTA! firestore reading - getPerformancesByDate ${this.config.collectionKey}`, promises.length)
        return Promise.all(promises)
      })
      .catch((err) => this.handleError(err, null))
  }

  addSkillPerson(performanceId: string, personId: string, skill: string): Promise<boolean> {
    return this.firestore
      .collection(this.config.collectionKey)
      .doc(performanceId)
      .set({ personsSkills: { [personId]: arrayUnion(skill) } }, { merge: true })
      .then(() => {
        console.log("!QUOTA! firestore writing addSkillPerson", 1)
        const cache = this.cache[performanceId]
        if (cache) {
          cache.personsSkills = cache.personsSkills || {}
          const cacheSkills = cache.personsSkills
          cacheSkills[personId] = cacheSkills[personId] || []
          cacheSkills[personId].push(skill)
        }
        return true
      })
      .catch((err) => this.handleError(err, false))
  }

  deleteSkillPerson(performanceId: string, personId: string, skill: string): Promise<boolean> {
    return this.firestore
      .collection(this.config.collectionKey)
      .doc(performanceId)
      .set({ personsSkills: { [personId]: arrayRemove(skill) } }, { merge: true })
      .then(() => {
        const cache = this.cache[performanceId]
        if (cache) {
          cache.personsSkills = cache.personsSkills || {}
          const cacheSkills = cache.personsSkills
          const array = cacheSkills[personId] || []
          const index = array.findIndex((x: string) => x === skill)
          if (index >= 0) {
            array.splice(index, 1)
          }
        }
        return true
      })
      .catch((err) => this.handleError(err, false))
  }

  getDancersIds(performance: PerformanceDTO): Promise<Array<string>> {
    return this.refService.getDancerSkillId2().then((dancerSkillId) => {
      return Object.keys(performance?.personsSkills || {}).filter((personId) =>
        (performance?.personsSkills[personId] || []).find((skillId) => skillId === dancerSkillId),
      )
    })
  }

  getPerformancesWithProgramVersion(programVersionId: string): Promise<Array<PerformanceDTO>> {
    return this.firestore
      .collection(this.config.collectionKey, (ref) => ref.where("program_version_id", "==", programVersionId))
      .get()
      .toPromise()
      .then((res) => {
        const items: Array<Promise<PerformanceDTO>> = []
        res?.forEach((r) => items.push(this._toDTO(r, FireStoreService.ACTION.GET)))
        console.log(
          `!QUOTA! firestore reading - getPerformancesWithProgramVersion ${this.config.collectionKey}`,
          items.length,
        )
        return Promise.all(items).then((items) => {
          items.sort((a, b) => (a.date < b.date ? 1 : -1))
          console.log("getPerformancesWithProgramVersion", items)
          return items
        })
      })
      .catch((err) => this.handleError(err, null))
  }

  async filteringPerformancesByIncludingSeniority(perfs: Array<PerformanceDTO>) {
    return this._filteringPerformancesByStatusProps(perfs, "seniorityExcluded", false)
  }

  private async _filteringPerformancesByStatusProps(perfs: Array<PerformanceDTO>, field: string, value = true) {
    const statusPerf = await this.refService.getAllFromCache(REFERENTIAL.perf_status)
    return perfs.filter((p) => {
      const status = statusPerf.find((x) => x.id === p.status.id) as any
      return status ? status[field] == value : false
    })
  }

  public initCommunication(perf: PerformanceDTO, personIds: Array<string>) {
    perf.communication = perf.communication || {}
    personIds.forEach((personId) => {
      perf.communication[personId] = perf.communication[personId] || {
        town: false,
        date: false,
        planning: false,
        programing: false,
        status: null,
        synchro_calendar: false,

        notes: [],

        //deprecated
        external_note: "",
        external_note_prio: "",
        note: "",
      }
      //Some data could been created without notes
      perf.communication[personId].notes = perf.communication[personId].notes || []
      if (perf.communication[personId].town === undefined && perf.communication[personId].date === true)
        perf.communication[personId].town = true
    })
  }
}

export interface DistributionDTO {
  // Some distribution still using these fields!!
  // roleId?: string => legacy
  // roleSubstituteId?: string => legacy
  // coefSubstitute?: number => legacy

  roles: Array<{ id: string }>
  substitutes: Array<{ id: string; coef: number }>

  // rôles/places des parties (PartDTO), stockés à part pour ne pas perturber les lecteurs de roles/substitutes
  partRoles?: Array<{ id: string }>
  partSubstitutes?: Array<{ id: string; coef: number }>
}

export type ExternNoteType = "prio" | "normal" | "internal" | "status"
export const mapExternNoteTypeLabel = {
  normal: "Normal",
  prio: "Prioritaire",
  internal: "Interne",
}

export interface ExternNote {
  id: string
  type: ExternNoteType
  text: string
  date: Date
  datePublic: Date
  statusRef?: { id: string }
}

export interface PerformanceDTO {
  id: string
  status_unexpected: { id: string }
  season: { id: string }
  code: { id: string }
  type: { id: string }
  date: Date
  month: string
  year: string
  hour: string
  hour_date: Date
  town: string
  department: string
  country: string
  location: string
  nb_dancer: string
  status: { id: string }
  program: { id: string }
  program_version_id: string
  program_configuration_id: string // legacy
  note_budget: string
  currency: { id: string }
  amount: string
  amount_note: string
  additional_costs: string
  V: string
  H: string
  R: string
  gauge: string
  artistic_director: { id: string }
  director: { id: string }
  DA: string
  note: string
  video_storage: [{ id: string }]
  video_notes: string
  seniority_ignore: boolean
  cessionDA: number
  AV_DD: number

  distribution_note: string
  distribution?: DistributionPerfDTO
  personsSkills: { [personId: string]: Array<string> /*skillId*/ }
  communication: {
    [personId: string]: {
      town: boolean
      date: boolean
      planning: boolean
      tour: boolean
      programing: boolean
      roles: boolean
      status?: { id: string }
      status_date?: Date
      program_title: boolean
      status_perf: boolean
      status_person: boolean
      synchro_calendar: boolean

      notes: Array<ExternNote>
      // Deprecated
      note: string
      external_note: string
      external_note_prio: string
    }
  }
  general_note: string

  configurationV2: ProgramConfigurationDTO

  programV2?: PerformanceProgramV2DTO

  // Calculated fields
  status_unexpected_label?: string
  season_label?: string
  name_label?: string
  code_label?: string
  status_label?: string
  status_label_color?: string
  status_label_unexpected?: string
  type_label?: string
  program_label?: string
  program_official_label?: string
  program_version_label?: string
  program_configuration_label?: string
  director_label?: string
  artistic_director_label?: string
  currency_label?: string
  video_storage_label?: string
  nb_dancer_calculated?: number
  seniority_ignore_label?: string
  v2_ref?: string // distribution de référence (compta, externes…) : V2 si programV2.active
}

export type DistributionPerfDTO = { [unitIdWithUnitVersionId: string]: { [personId: string]: DistributionDTO } }

// programme v2 de la représentation, indépendant des champs v1 (program_version_id, configurations, distribution)
export interface PerformanceProgramV2DTO {
  versionId: string // version isV2 du programme
  configuration: ProgramConfigurationDTO
  distribution: DistributionPerfDTO
  active?: boolean // la v2 fait référence (paiements, externes…)
}

// programV2 présenté sous les champs v1, pour réutiliser DistributionService tel quel ; undefined si non rattachée
export function perfAsV2(perf?: PerformanceDTO): PerformanceDTO | undefined {
  if (!perf?.programV2) return undefined
  return {
    ...perf,
    program_version_id: perf.programV2.versionId,
    program_configuration_id: "",
    configurationV2: perf.programV2.configuration,
    distribution: perf.programV2.distribution,
  }
}

// vue de référence pour les usages (paiements, contrats, stats, externes) : v2 si programV2.active, v1 sinon
export function performanceV1ouV2(perf: PerformanceDTO): PerformanceDTO {
  return (perf?.programV2?.active && perfAsV2(perf)) || perf
}

// inverse de perfAsV2 : programV2 à enregistrer à partir de la vue
export function programV2From(view: PerformanceDTO): PerformanceProgramV2DTO {
  return {
    ...view.programV2,
    versionId: view.program_version_id,
    configuration: view.configurationV2,
    distribution: view.distribution || {},
  }
}

export interface PerformanceInfoExtern {
  distribution?: { [unitIdWithUnitVersionId: string]: { [personId: string]: DistributionDTO } }
  program: { id: string }
  program_version_id: string
  program_configuration_id: string // legacy

  date: string // iso format
  dateSorting: string
  hour: string
  dateStr: string
  town: string
  code: { id: string }
  personsSkills: { [personId: string]: Array<string> /*skillId*/ }
  season: { id: string }
  paymentMin: string
  paymentReal: string
  distribution_note: string
  showTour: boolean
  roles: boolean
  programing: boolean
  showPlanning: boolean
  program_title: boolean
  general_note: string
  myStatus?: { id: string }
  status_perf?: string
  type_perf?: string
  persons: { [personId: string]: string /*fisrtName*/ }
  id: string

  notes: Array<{
    type: ExternNoteType
    date: Date
    text: string
    statusId: string
  }>
  note: string // deprecated
  note_prio: string // deprecated
}
